// eslint-disable-next-line no-restricted-imports
import {
  initIdentityClient as init,
  signinRedirect as _signinRedirect,
  signinPopup as _signinPopup,
  signinSilent as _signinSilent,
  completeSignoutSilent as _completeSignoutSilent,
  completeSignin as _completeSignin,
  type SigninRedirectArgs,
  type SigninPopupArgs,
  hasValidUserSession as _hasValidUserSession,
  getAccessToken as _getAccessToken,
  getUserManager,
  getUserProfile as _getUserProfile,
  getUserId as _getUserId,
  isEmployee as _isEmployee,
  getAccessTokenSync as _getAccessTokenSync,
  removeUser as _removeUser,
  signoutSilent as _signoutSilent,
  onUserLoaded as _onUserLoaded,
  onUserUnloaded as _onUserUnloaded,
  onAccessTokenExpired as _onAccessTokenExpired,
} from '@onxmaps/identity-client'
import pubsub from '@/lib/pubsub'
import { waitForFocus } from '@/lib/browser'
// NOTE: identity-events imports gtm/marketing which import identity-client — circular.
// Break the cycle by importing identity-events lazily inside the functions that need it.
import type { IdentityInitiationMarketingProps } from '@/services/identity-events'
import type { IdentityInitiationIntent } from '@/services/identity-types'
import { isGroupEnabled, isLoggingEnabled, info, error } from '@/lib/logger'
import { isActive } from '@/services/feature-flag-service'

export type SigninState = { after_login?: string }
type SigninRedirectArgsWithState = SigninRedirectArgs & { state?: SigninState }
type SigninPopupArgsWithState = SigninPopupArgs & { state?: SigninState }

let isInitialized = false

function initIdentityClient(isSilentRenewEnabled = true) {
  const { authority, client_id } = window.environment

  if (isInitialized) {
    return
  }

  init({
    authority,
    client_id,
    redirect_uri: `${window.origin}/auth/callback`,
    scope: 'openid email profile internal',
    silent_redirect_uri: `${window.origin}/auth/silent-renew`,
    post_logout_redirect_uri: `${window.origin}/auth/post-logout`,
    logs_enabled: isGroupEnabled('Identity') && isLoggingEnabled.value,
    automaticSilentRenew: isSilentRenewEnabled,
    // 2 minutes
    silentRenewTimeInSecondsBeforeAccessTokenExpiration: 120,
    // We don't need to load user info it comes from the ID token
    loadUserInfo: false,
    // Listen for localStorage user changes and dispatch userLoaded events for cross-tab sync
    observeLocalStorageUserChanges: true,
  })

  if (isActive('debug_identity_client')) {
    window.id_client = getUserManager()
  }
  isInitialized = true
}

/**
 * Saves the intended after login URL in case of a callback error
 * @internal
 * @private
 */
export function saveAfterLoginUrlInCaseOfCallbackError(
  args: SigninRedirectArgsWithState | SigninPopupArgsWithState = {}
) {
  try {
    const after_login = args.state?.after_login ?? window.location.href
    localStorage.setItem('onx-identity-after-login', after_login)
  } catch {
    // ignore storage error
  }
}

/**
 * Used to get the intended after login URL in case a identity callback error
 * is encountered so that we can allow the user to try again and goto the correct
 * page.
 *
 * @note Should not be used on successful identity callback, use the state
 * returned in the callback instead since it is verified.
 */
export function getAfterLoginUrlInCaseOfCallbackError(): string {
  try {
    const after_login = localStorage.getItem('onx-identity-after-login')

    return after_login ?? '/hunt'
  } catch {
    // ignore storage error
  }

  return '/hunt'
}

/**
 * Wrapper around identity-client signinRedirect with some included marketing events
 *
 * @note the success event for this is called in app.vue::created
 */
export const signinRedirect = async (
  args: SigninRedirectArgsWithState,
  {
    userIntent,
    marketingProps,
  }: {
    userIntent?: IdentityInitiationIntent
    marketingProps?: IdentityInitiationMarketingProps
  } = {}
): Promise<boolean> => {
  // Wait for the tab to become focused before continuing to prevent multiple
  // redirects across tabs.  Only one tab needs to redirect to identity and auth.
  await waitForFocus()
  initIdentityClient()

  if (await _hasValidUserSession()) {
    // Skip the redirect if we already have a valid session
    // This can happen if the user is already signed in
    // or if the silent renew has already happened
    // use document.location.reload() to refresh the page instead of vue router
    // which causes a navigation failure
    document.location.reload()
    return false
  }
  saveAfterLoginUrlInCaseOfCallbackError(args)

  const { logIdentityInitiation } = await import('@/services/identity-events')
  if (marketingProps) {
    logIdentityInitiation(userIntent, marketingProps)
  } else {
    logIdentityInitiation(userIntent)
  }
  _signinRedirect(args)
  return false
}

/**
 * Wrapper around identity-client signinPopup with some included marketing events
 */
export const signinPopup = async (
  args: SigninPopupArgsWithState,
  {
    userIntent,
    marketingProps,
  }: {
    userIntent?: IdentityInitiationIntent
    marketingProps?: IdentityInitiationMarketingProps
  } = {}
) => {
  initIdentityClient()
  saveAfterLoginUrlInCaseOfCallbackError(args)
  const { logIdentityInitiation, logIdentityResult, IdentityResult } =
    await import('@/services/identity-events')
  try {
    if (marketingProps) {
      logIdentityInitiation(userIntent, marketingProps)
    } else {
      logIdentityInitiation(userIntent)
    }
    const result = await _signinPopup(args)
    logIdentityResult(IdentityResult.Success)
    return result
  } catch (e: unknown) {
    logIdentityResult(IdentityResult.Dismiss)
    throw e
  }
}

/**
 * Remove current user from storage or log out silently from identity server
 * @param options - forUnauthorizedRequest boolean
 * @return promise that resolves when user is logged out
 */
export function removeUser({
  forUnauthorizedRequest = false,
} = {}): Promise<void> {
  initIdentityClient()
  if (forUnauthorizedRequest) {
    return _removeUser()
  }

  const logOutPromise = _signoutSilent()
  logOutPromise.finally(() => {
    pubsub.publish('logoutRequestCompleted')
  })
  return logOutPromise
}

/**
 * *(AI-generated)*
 * Initiates a silent sign-in to refresh the user session.
 */
export function signinSilent() {
  initIdentityClient()
  return _signinSilent()
}

/**
 * *(AI-generated)*
 * Returns the current user ID.
 */
export async function getUserId() {
  initIdentityClient()
  return await _getUserId()
}

/**
 * *(AI-generated)*
 * Completes the sign-in callback flow.
 */
export async function completeSignin(isSilentRenewEnabled: boolean = true) {
  initIdentityClient(isSilentRenewEnabled)
  return await _completeSignin()
}

/**
 * *(AI-generated)*
 * Returns the current access token.
 */
export async function getAccessToken() {
  initIdentityClient()
  return await _getAccessToken()
}

/**
 * *(AI-generated)*
 * Returns the current access token synchronously.
 */
export function getAccessTokenSync() {
  initIdentityClient()
  return _getAccessTokenSync()
}

/**
 * *(AI-generated)*
 * Returns the current user profile.
 */
export async function getUserProfile() {
  initIdentityClient()
  return await _getUserProfile()
}

/**
 * *(AI-generated)*
 * Returns whether the current user is an employee.
 */
export async function isEmployee() {
  initIdentityClient()
  return await _isEmployee()
}

/**
 * *(AI-generated)*
 * Completes a silent signout flow.
 */
export function completeSignoutSilent() {
  initIdentityClient()
  return _completeSignoutSilent()
}

/**
 * *(AI-generated)*
 * Returns whether the current user has a valid session.
 */
export function hasValidUserSession() {
  initIdentityClient()
  return _hasValidUserSession()
}

/**
 * *(AI-generated)*
 * Registers a callback to be called when the user is loaded.
 */
export function onUserLoaded(callback: () => void) {
  initIdentityClient()
  return _onUserLoaded(callback)
}
/**
 * *(AI-generated)*
 * Registers a callback to be called when the user is unloaded.
 */
export function onUserUnloaded(callback: () => void) {
  initIdentityClient()
  return _onUserUnloaded(callback)
}

/**
 * @internal
 * For testing purposes only - forces the token to be near expiration to
 * trigger silent renew logic.
 *
 * This is useful for testing the silent renew flow without having to wait
 * for the token to naturally expire.
 */
export function forceSilentRenewTokenExpiration() {
  initIdentityClient()
  const manager = getUserManager()
  const userKey = `oidc.user:${manager.settings.authority}:${manager.settings.client_id}`
  const user = localStorage.getItem(userKey)

  if (user) {
    const userData = JSON.parse(user)
    info('Identity', 'Current user data:', userData)
    info('Identity', 'Current time:', Date.now() / 1000)
    info('Identity', 'Current expires_at:', userData.expires_at)

    // Set the access token to expire in 10 seconds to trigger immediate renewal
    userData.expires_at = Date.now() / 1000 + 10
    localStorage.setItem(userKey, JSON.stringify(userData))

    info('Identity', 'Token expiration set to:', userData.expires_at)
    info(
      'Identity',
      'Simulated token expiration for silent renew - token will expire in 10 seconds'
    )

    // Force the user manager to check the token expiration
    manager
      .getUser()
      .then((user) => {
        info('Identity', 'User after expiration update:', user)
      })
      .catch((e) => {
        error('Identity', 'Error getting user after expiration update:', e)
      })
  } else {
    info('Identity', 'No user found in localStorage for key:', userKey)
    info('Identity', 'Available localStorage keys:', Object.keys(localStorage))
  }
}

/**
 * Add a listener for the access token expired event.
 * @param listener - The listener function to call when the event occurs.
 */
export function onAccessTokenExpired(listener: () => void) {
  initIdentityClient()
  return _onAccessTokenExpired(listener)
}

/**
 * @internal
 * Publish events to show/hide the session expired modal. The component itself
 * listens and teleports into #modal-wrapper (Vue 3 Teleport) so we avoid
 * createApp instantiation here and ensure only one instance.
 */
export function showSessionExpiredModal() {
  pubsub.publish('show_session_expired_modal')
}

/**
 * *(AI-generated)*
 * Publishes an event to hide the session expired modal.
 */
export function hideSessionExpiredModal() {
  pubsub.publish('hide_session_expired_modal')
}
